import _ from 'lodash';

import {
    GetFundingRequirementsCreditFacilityResponse,
    GetFundingRequirementsFlexi24Response,
    GetFundingRequirementsIwocapayResponse,
    GetFundingRequirementsMerchantCashAdvanceResponse
} from '@iwoca/lapi-client/edge';

export type TDeserialisedFundingRequirements = {
    type:
        | 'DEBIT_CARD'
        | 'BANK_DETAILS'
        | 'PENNY_TRANSFER'
        | 'IDENTITY_QUESTION'
        | 'ONFIDO_VERIFICATION'
        | 'IDNOW'
        | 'MAJOR_SHAREHOLDER_CHECK'
        | 'BIP'
        | 'APPLICANT_CALL';
    status: 'OPTIONAL' | 'REQUIRED' | 'COMPLETED' | 'ERROR' | 'PENDING';
}[];

type TFundingRequirements =
    | GetFundingRequirementsCreditFacilityResponse
    | GetFundingRequirementsFlexi24Response
    | GetFundingRequirementsIwocapayResponse
    | GetFundingRequirementsMerchantCashAdvanceResponse;

type TFundingRequirement =
    | GetFundingRequirementsCreditFacilityResponse['data'][0]
    | GetFundingRequirementsFlexi24Response['data'][0]
    | GetFundingRequirementsIwocapayResponse['data'][0]
    | GetFundingRequirementsMerchantCashAdvanceResponse['data'][0];

function getActionFromFundingRequirement(
    funding_requirement: TFundingRequirement
) {
    switch (funding_requirement.recommended_resolution_method) {
        case 'add_default_payment_method':
            return 'DEBIT_CARD';
        case 'link_bank_account':
        case 'check_linked_direct_debit':
            return 'BANK_DETAILS';
        case 'complete_penny_transfer':
        case 'complete_penny_transfer_with_signature':
            return 'PENNY_TRANSFER';
        case 'onfido_check':
            return 'ONFIDO_VERIFICATION';
        case 'id_now_check':
            return 'IDNOW';
        case 'major_shareholders_check':
            return 'MAJOR_SHAREHOLDER_CHECK';
        case 'check_customer_does_not_exceed_bip_cap':
            return 'BIP';
        default:
            return 'APPLICANT_CALL';
    }
}

function showUniqueFundingRequirementsWithCorrectStatus(
    fundingRequirements: TDeserialisedFundingRequirements
): TDeserialisedFundingRequirements {
    return fundingRequirements.reduce((requirements: any, requirement: any) => {
        const existingDuplicateRequirement = _.findIndex(requirements, {
            type: requirement.type
        });
        if (existingDuplicateRequirement === -1) {
            return [...requirements, requirement];
        }

        const STATUS_PRIORITY_ORDER = {
            REQUIRED: 1,
            ERROR: 2,
            PENDING: 3,
            COMPLETED: 4
        };

        if (
            STATUS_PRIORITY_ORDER[
                requirements[existingDuplicateRequirement]
                    .status as keyof typeof STATUS_PRIORITY_ORDER
            ] >
            STATUS_PRIORITY_ORDER[
                requirement.status as keyof typeof STATUS_PRIORITY_ORDER
            ]
        ) {
            requirements[existingDuplicateRequirement] = requirement;
        }

        return requirements;
    }, []);
}

const completeApplicationStatus = {
    optional: 'OPTIONAL',
    required: 'REQUIRED',
    completed: 'COMPLETED',
    error: 'ERROR',
    pending: 'PENDING'
};

const getCompleteApplicationStatus = (
    requirement: GetFundingRequirementsCreditFacilityResponse['data'][0]
) => {
    // @ts-expect-error - EDGE schema is wrong for funding_requirements, as it doesn't have the is_optional flag on it
    // This error can be resolved by updating Django (schema) and the lapi client (same goes for the error in the return)
    if (requirement.status === 'required' && requirement.is_optional === true) {
        return completeApplicationStatus.optional;
    }

    return completeApplicationStatus[requirement.status];
};

export const deserialiseFundingRequirements = (
    funding_requirements: TFundingRequirements['data']
): TDeserialisedFundingRequirements => {
    const deserialisedFundingRequirements = funding_requirements
        .filter(
            (requirement) =>
                requirement.category !== 'contractual' ||
                (requirement.category === 'contractual' &&
                    requirement.recommended_resolution_method ===
                        'complete_repayment_pledge')
        )
        .map((funding_requirement) => ({
            type: getActionFromFundingRequirement(
                funding_requirement
            ) as TDeserialisedFundingRequirements[0]['type'],
            status: getCompleteApplicationStatus(
                funding_requirement
            ) as TDeserialisedFundingRequirements[0]['status']
        }));

    const uniqueFundingRequirements =
        showUniqueFundingRequirementsWithCorrectStatus(
            deserialisedFundingRequirements
        );

    return uniqueFundingRequirements;
};
